/**
 * Coach Hooks
 *
 * TanStack Query hooks + SSE streaming utility for the AI Coach API.
 * Follows the same patterns as useOperatorPractice.ts.
 *
 * Standard TanStack Query hooks:
 * - useCreateSession() -- mutation, POST /api/operator/coach/sessions
 * - useEndSession() -- mutation, POST /api/operator/coach/sessions/:id/end
 * - useCoachSessions() -- query, GET /api/operator/coach/sessions (paginated)
 * - useCoachSession(sessionId) -- query, GET /api/operator/coach/sessions/:id
 * - useCoachUsage() -- query, GET /api/operator/coach/usage
 *
 * SSE streaming function (raw fetch, NOT TanStack):
 * - streamMessage() -- POST /api/operator/coach/sessions/:id/messages (SSE stream)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
import { useApi } from '../lib/hooks/useApi';
import type {
  CreateCoachSession,
  CoachSessionListItem,
  CoachSessionDetail,
  CoachUsage,
  CoachAction,
  CoachSessionMode,
} from '@guild/shared';

// ============================================
// TYPES (response shapes not in @guild/shared)
// ============================================

/** Response from POST /api/operator/coach/sessions */
interface CreateSessionResponse {
  sessionId: string;
  firstMessage: string;
  mode: CoachSessionMode;
  suggestedFollowups?: string[];
  startedAt: string;
  maxMessages: number;
  messageCount: number;
}

/** Response from POST /api/operator/coach/sessions/:id/end */
interface EndSessionResponse {
  summary: string;
  sessionId: string;
}

/** Result from streaming a message (the 'done' SSE event payload) */
export interface StreamMessageResult {
  content: string;
  actions?: CoachAction[];
  suggestedFollowups?: string[];
  messageCount?: number;
  messagesRemaining?: number;
  sessionEnded?: boolean;
  sessionSummary?: string;
}

// ============================================
// QUERY KEYS
// ============================================

export const coachKeys = {
  all: ['operator-coach'] as const,
  sessions: () => [...coachKeys.all, 'sessions'] as const,
  session: (sessionId: string) => [...coachKeys.all, 'session', sessionId] as const,
  usage: () => [...coachKeys.all, 'usage'] as const,
};

// ============================================
// QUERIES
// ============================================

/**
 * Fetch paginated list of coach sessions for the operator.
 */
export function useCoachSessions() {
  const api = useApi();

  return useQuery({
    queryKey: coachKeys.sessions(),
    queryFn: () =>
      api.get<CoachSessionListItem[]>('/api/operator/coach/sessions'),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Fetch a single session with all messages.
 * Only enabled when sessionId is provided.
 */
export function useCoachSession(sessionId: string | null) {
  const api = useApi();

  return useQuery({
    queryKey: coachKeys.session(sessionId ?? ''),
    queryFn: () =>
      api.get<CoachSessionDetail>(`/api/operator/coach/sessions/${sessionId!}`),
    enabled: !!sessionId,
    staleTime: 30 * 1000, // 30 seconds -- messages are mostly local state
  });
}

/**
 * Fetch coach usage limits for the operator.
 */
export function useCoachUsage() {
  const api = useApi();

  return useQuery({
    queryKey: coachKeys.usage(),
    queryFn: () => api.get<CoachUsage>('/api/operator/coach/usage'),
    staleTime: 60 * 1000, // 1 minute
  });
}

// ============================================
// MUTATIONS
// ============================================

/**
 * Create a new coach session.
 * Invalidates sessions list and usage on success.
 */
export function useCreateSession() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCoachSession) =>
      api.post<CreateSessionResponse>('/api/operator/coach/sessions', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: coachKeys.sessions() });
      queryClient.invalidateQueries({ queryKey: coachKeys.usage() });
    },
  });
}

/**
 * End a coach session.
 * Invalidates sessions list, specific session, and usage on success.
 */
export function useEndSession() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) =>
      api.post<EndSessionResponse>(`/api/operator/coach/sessions/${sessionId}/end`),
    onSuccess: (_data, sessionId) => {
      queryClient.invalidateQueries({ queryKey: coachKeys.sessions() });
      queryClient.invalidateQueries({ queryKey: coachKeys.session(sessionId) });
      queryClient.invalidateQueries({ queryKey: coachKeys.usage() });
    },
  });
}

// ============================================
// SSE STREAMING (raw fetch, not TanStack)
// ============================================

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

/**
 * Send a message and stream the coach's response via SSE.
 *
 * Uses raw fetch() with manual Authorization header (not useApi,
 * because we need access to the ReadableStream body for SSE parsing).
 *
 * Phase 31 chunk G: extended to support optional `onThinking` callback for
 * the new 'thinking' SSE event variant emitted by the Gemini agent loop
 * during tool dispatch. The anthropic-provider path NEVER emits 'thinking'
 * events, so callers that don't pass `onThinking` (or sessions on the
 * anthropic path) see no behavior change.
 *
 * Consumer state machine (owned by CoachPage.tsx — see chunk I):
 * - 'thinking' event → set pill status
 * - First 'token' event after a 'thinking' → clear pill status
 * - 'done' / 'error' event → clear pill status
 *
 * 10-second fallback (EM M2): if the upstream agent's first SSE event is
 * delayed beyond ~10s, the existing <TypingIndicator /> in CoachPage renders
 * naturally because streamingContent === '' until tokens arrive. No explicit
 * timeout logic is needed at this layer — keep the parser simple.
 *
 * @param sessionId - Active session ID
 * @param content - Operator's message text
 * @param token - Auth0 bearer token
 * @param onToken - Callback invoked for each streamed text delta
 * @param onThinking - Optional callback invoked for each 'thinking' event
 *   with the operator-facing status string (never tool names). Multiple
 *   thinking events in a row replace, not stack.
 * @returns The final 'done' event data with full content + parsed actions/followups
 */
export async function streamMessage(
  sessionId: string,
  content: string,
  token: string,
  onToken: (text: string) => void,
  onThinking?: (status: string) => void,
): Promise<StreamMessageResult> {
  const response = await fetch(
    `${API_BASE_URL}/api/operator/coach/sessions/${sessionId}/messages`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ content }),
    },
  );

  if (!response.ok) {
    // Try to parse error body
    let errorMessage = 'Failed to send message';
    let errorCode: string | undefined;
    try {
      const errorData = await response.json();
      errorMessage = errorData?.error?.message || errorData?.error || errorMessage;
      errorCode = errorData?.error?.code || errorData?.errorCode;
    } catch {
      // Response body may not be JSON for streaming errors
    }
    const err = new Error(errorMessage);
    // Attach status and errorCode for frontend differentiation
    (err as Error & { status?: number; errorCode?: string }).status = response.status;
    (err as Error & { status?: number; errorCode?: string }).errorCode = errorCode;
    throw err;
  }

  if (!response.body) {
    throw new Error('No response body received');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    // Parse SSE events from buffer (events are separated by double newlines)
    const parts = buffer.split('\n\n');
    // Keep the last (potentially incomplete) part in buffer
    buffer = parts.pop() || '';

    for (const part of parts) {
      const lines = part.split('\n');
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const event = JSON.parse(line.slice(6));

            if (event.type === 'token') {
              onToken(event.content);
            }

            if (event.type === 'thinking') {
              // Phase 31 chunk G: forward operator-facing status pill text to
              // the consumer. `event.status` comes from the agent's
              // AGENT_CONFIG.COACH.TOOL_PILL_TEXT/MULTI_TOOL_PILL_TEXT — never
              // raw tool names. Silently ignore if no callback was passed.
              onThinking?.(event.status);
            }

            if (event.type === 'done') {
              return {
                content: event.content,
                actions: event.actions,
                suggestedFollowups: event.suggestedFollowups,
                messageCount: event.messageCount,
                messagesRemaining: event.messagesRemaining,
                sessionEnded: event.sessionEnded,
                sessionSummary: event.sessionSummary,
              };
            }

            if (event.type === 'error') {
              throw new Error(event.content);
            }
          } catch (parseErr) {
            // Re-throw errors we threw ourselves (from type === 'error' events);
            // silently skip genuine JSON parse failures on malformed SSE lines
            if (parseErr instanceof SyntaxError) {
              // JSON.parse failed — malformed line, skip it
              continue;
            }
            // This was an error we threw (from event.type === 'error'), propagate it
            throw parseErr;
          }
        }
      }
    }
  }

  // If we reach here without a 'done' event, the stream ended unexpectedly
  throw new Error('Stream ended without completion');
}

/**
 * Hook that returns the streamMessage function pre-bound with the auth token.
 * This avoids the need for consumers to manage token retrieval.
 *
 * Phase 31 chunk G: optional `onThinking` callback threaded through for the
 * Gemini agent's 'thinking' SSE events. Anthropic-path consumers can omit it.
 */
export function useStreamMessage() {
  const { getAccessTokenSilently } = useAuth0();

  return useCallback(
    async (
      sessionId: string,
      content: string,
      onToken: (text: string) => void,
      onThinking?: (status: string) => void,
    ): Promise<StreamMessageResult> => {
      const token = await getAccessTokenSilently();
      return streamMessage(sessionId, content, token, onToken, onThinking);
    },
    [getAccessTokenSilently],
  );
}
